/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { ReactNode, FC, memo, useEffect, useRef, KeyboardEvent as ReactKeyboardEvent } from "react";
import "./modal.scss";
import { Button } from "@components/button";
import { createPortal } from "react-dom";
import { CloseIcon } from "@components/icons";
import { useOutsideClick } from "@hooks/use-outside-click";

type ModalProps = {
  children: ReactNode;
  onClose?: () => void;
  isOpen: boolean;
  placement?: "right" | "center" | "middle" | "bottom-right" | "bottom-left";
  closePlacement?: "right" | "left";
  closeIconColor?: "#FFFFFF" | "#00033e";
  closeTop?: string;
  width?: string;
  allowCloseOnOutsideClick?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
};

const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const ModalComponent: FC<ModalProps> = ({
  children,
  onClose,
  isOpen,
  placement,
  closePlacement = "right",
  closeIconColor = "#00033e",
  closeTop,
  width,
  allowCloseOnOutsideClick = false,
  ariaLabel,
  ariaLabelledBy,
}) => {
  const container = document.querySelector("#ul-modal");
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const ref = useOutsideClick<HTMLDivElement>(() => {
    if (allowCloseOnOutsideClick && isOpen && onClose) {
      onClose();
    }
  });

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const wrapper = ref.current;
    if (wrapper) {
      const firstFocusable = wrapper.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? wrapper).focus();
    }
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen, ref]);

  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const wrapper = ref.current;
    if (!wrapper) return;
    const focusables = Array.from(wrapper.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
      e.preventDefault();
      wrapper.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === wrapper)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!isOpen || !container) {
    return null;
  }
  return createPortal(
    <>
      <div className="modal-mask" aria-hidden="true" />
      <div className={`modal-container ${placement ?? ""}`}>
        <div
          ref={ref}
          className="modal-wrapper"
          style={{ width }}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          {onClose ? (
            <Button
              onClick={onClose}
              className={`close ${closePlacement}`}
              variant="close"
              style={{ top: closeTop }}
              aria-label="Close"
            >
              <CloseIcon color={closeIconColor} size={16} />
            </Button>
          ) : null}
          {children}
        </div>
      </div>
    </>,
    container,
  );
};

export const Modal = memo(ModalComponent);
